import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tool } from '../../types';
import { useAuth } from '../../context/AuthContext';
import Input from '../ui/Input';
import Select from '../ui/Select';
import Button from '../ui/Button';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from '../ui/Card';
import { Trash, Camera, X, Upload, Eye } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface ToolFormProps {
  tool?: Tool;
  isEdit?: boolean;
}

const ToolForm: React.FC<ToolFormProps> = ({ 
  tool,
  isEdit = false
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    location: '',
    project_code: '',
    status: 'available',
    category: '',
    serial_number: '',
    purchase_date: '',
    last_maintenance: '',
    assigned_to: '',
    notes: '',
    images: '',
  });
  
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (tool && isEdit) {
      setFormData({
        name: tool.name,
        description: tool.description || '',
        location: tool.location,
        project_code: tool.project_code || '',
        status: tool.status,
        category: tool.category || '',
        serial_number: tool.serial_number || '',
        purchase_date: tool.purchase_date || '',
        last_maintenance: tool.last_maintenance || '',
        assigned_to: tool.assigned_to || '',
        notes: tool.notes || '',
        images: Array.isArray(tool.images) ? tool.images.join(', ') : (tool.images || ''),
      });
    }
  }, [tool, isEdit]);


  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Tool name is required';
    }
    
    if (!formData.location.trim()) {
      newErrors.location = 'Location is required';
    }
    
    if (formData.purchase_date && new Date(formData.purchase_date) > new Date()) {
      newErrors.purchase_date = 'Purchase date cannot be in the future';
    }
    
    if (formData.last_maintenance && new Date(formData.last_maintenance) > new Date()) {
      newErrors.last_maintenance = 'Maintenance date cannot be in the future';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSelectChange = (name: string) => (value: string) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      const toolData = {
        ...formData,
        images: formData.images || null,
        user_id: user?.id,
      };

      if (isEdit && tool) {
        try {
          const { error } = await supabase
            .from('tools')
            .update(toolData)
            .eq('id', tool.id);

          if (error) throw error;
        } catch (error: any) {
          console.error('Error updating tool:', error);
          throw error;
        }
      } else {
        try {
          const { error } = await supabase
            .from('tools')
            .insert([toolData]);

          if (error) throw error;
        } catch (error: any) {
          console.error('Error creating tool:', error);
          throw error;
        }
      }
      
      navigate('/tools');
    } catch (error: any) {
      console.error('Error saving tool:', error);
      setErrors(prev => ({
        ...prev,
        submit: error.message || 'Failed to save tool. Please try again.'
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteTool = () => {
    if (!tool) return;
    
    if (window.confirm('Are you sure you want to delete this tool? This action cannot be undone.')) {
      deleteTool();
    }
  };

  const deleteTool = async () => {
    if (!tool) return;

    try {
      const { error } = await supabase
        .from('tools')
        .delete()
        .eq('id', tool.id);

      if (error) throw error;
      
      navigate('/tools');
    } catch (error) {
      console.error('Error deleting tool:', error);
      setErrors(prev => ({
        ...prev,
        submit: 'Failed to delete tool. Please try again.'
      }));
    }
  };

  const statusOptions = [
    { value: 'available', label: 'Available' },
    { value: 'in_use', label: 'In Use' },
    { value: 'maintenance', label: 'Maintenance' },
    { value: 'retired', label: 'Retired' },
  ];

  const categoryOptions = [
    { value: '', label: 'Select Category' },
    { value: 'Measurement', label: 'Measurement' },
    { value: 'Analysis', label: 'Analysis' },
    { value: 'Assembly', label: 'Assembly' },
    { value: 'Manufacturing', label: 'Manufacturing' },
    { value: 'Inspection', label: 'Inspection' },
    { value: 'Safety', label: 'Safety' },
    { value: 'Computing', label: 'Computing' },
    { value: 'Other', label: 'Other' },
  ];

  return (
    <Card className="max-w-4xl mx-auto">
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle>{isEdit ? 'Edit Tool' : 'Add New Tool'}</CardTitle>
          {isEdit && tool && (
            <Button
              type="button"
              variant="danger"
              onClick={handleDeleteTool}
            >
              <Trash className="h-4 w-4 mr-2" />
              Delete Tool
            </Button>
          )}
        </div>
      </CardHeader>
      <form onSubmit={handleSubmit}>
        <CardContent className="space-y-6">
          {/* Basic Information */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Tool Name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                error={errors.name}
                fullWidth
                required
              />
              
              <Select
                label="Category"
                options={categoryOptions}
                value={formData.category}
                onChange={handleSelectChange('category')}
                error={errors.category}
                fullWidth
              />
            </div>
            
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                name="description"
                rows={3}
                value={formData.description}
                onChange={handleInputChange}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-blue-900"
                placeholder="Detailed description of the tool and its capabilities"
              />
            </div>
          </div>

          {/* Location and Assignment */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Location & Assignment</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Current Location"
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                error={errors.location}
                placeholder="e.g., Electronics Lab - Bench 2"
                fullWidth
                required
              />
              
              <Input
                label="Project Code"
                name="project_code"
                value={formData.project_code}
                onChange={handleInputChange}
                placeholder="e.g., RD-2024-001"
                fullWidth
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <Select
                label="Status"
                options={statusOptions}
                value={formData.status}
                onChange={handleSelectChange('status')}
                fullWidth
                required
              />
              
              <Input
                label="Assigned To"
                name="assigned_to"
                value={formData.assigned_to}
                onChange={handleInputChange}
                placeholder="e.g., Dr. John Smith"
                fullWidth
              />
            </div>
          </div>

          {/* Technical Details */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Technical Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Serial Number"
                name="serial_number"
                value={formData.serial_number}
                onChange={handleInputChange}
                placeholder="e.g., DMM-2024-001"
                fullWidth
              />
              
              <Input
                label="Purchase Date"
                name="purchase_date"
                type="date"
                value={formData.purchase_date}
                onChange={handleInputChange}
                error={errors.purchase_date}
                fullWidth
              />
            </div>

            <div className="mt-4">
              <Input
                label="Last Maintenance Date"
                name="last_maintenance"
                type="date"
                value={formData.last_maintenance}
                onChange={handleInputChange}
                error={errors.last_maintenance}
                fullWidth
              />
            </div>
          </div>

          {/* Images */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Images</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Image URLs
                <span className="text-gray-500 font-normal ml-2">(comma-separated)</span>
              </label>
              <textarea
                name="images"
                rows={3}
                value={formData.images}
                onChange={handleInputChange}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-blue-900"
                placeholder="https://example.com/image1.jpg, https://example.com/image2.jpg"
              />
              <p className="mt-1 text-xs text-gray-500">
                Enter one or more image URLs separated by commas. For stock photos, try <a href="https://www.pexels.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Pexels</a>.
              </p>
              {formData.images && (
                <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
                  {formData.images.split(',').map((url, index) => {
                    const trimmedUrl = url.trim();
                    if (!trimmedUrl) return null;
                    return (
                      <div key={index} className="relative group">
                        <img
                          src={trimmedUrl}
                          alt={`Preview ${index + 1}`}
                          className="w-full h-24 object-cover rounded border border-gray-300"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23ddd" width="100" height="100"/%3E%3Ctext fill="%23999" font-family="sans-serif" font-size="12" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3EInvalid URL%3C/text%3E%3C/svg%3E';
                          }}
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Additional Notes */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Additional Information</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <textarea
                name="notes"
                rows={4}
                value={formData.notes}
                onChange={handleInputChange}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-blue-900"
                placeholder="Additional notes about the tool, maintenance requirements, usage instructions, etc."
              />
            </div>
          </div>


          {errors.submit && (
            <div className="text-sm text-red-600 bg-red-50 p-2 rounded">
              {errors.submit}
            </div>
          )}
        </CardContent>
        <CardFooter className="flex justify-between">
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate('/tools')}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            isLoading={isSubmitting}
          >
            {isEdit ? 'Update Tool' : 'Add Tool'}
          </Button>
        </CardFooter>
      </form>
    </Card>
  );
};

export default ToolForm;